/* RESPONSIBLE TEAM: team-phone */

import type AppService from 'embercom/services/app-service';
import Model, { attr } from '@ember-data/model';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { inject as service } from '@ember/service';
import moment from 'moment-timezone';
import { PhoneNumberType } from 'embercom/models/settings/calling';

export enum PhoneNumberStatus {
  Active = 'active',
  Porting = 'porting',
  MissingBundle = 'missing_bundle',
}

export type PhoneNumberRoutingMethod = 'workflow' | 'workspace' | 'team' | 'teammate';

export const COUNTRIES_WITH_STATE = 'US,DE,AU,AT,BR,IN,MY,MX,FM,MM,NZ,NG,PW,SS';

export enum PhoneNumberProvider {
  Intercom = 'intercom',
  AmazonConnect = 'amazon_connect',
  Other = 'other',
}

export default class PhoneNumber extends Model {
  @service declare appService: AppService;

  @attr('string') declare name: string;
  @attr('string') declare phoneNumber: string;
  @attr('string') declare countryIsoCode: string;
  @attr('number') declare phoneType: number;
  @attr('string') declare status: PhoneNumberStatus;
  @attr('boolean') declare inboundEnabled: boolean;
  @attr('boolean') declare outboundEnabled: boolean;
  @attr('boolean') declare inboundCapable: boolean;
  @attr('boolean') declare outboundCapable: boolean;
  @attr('string') declare welcomeMessage: string;
  @attr('string') declare routingMethod: PhoneNumberRoutingMethod;
  @attr('string') declare assignTeamId: string;
  @attr('string') declare assignAdminId: string;
  @attr('boolean') declare voicemailDeflectionEnabled: boolean;
  @attr('string') declare voicemailDeflectionGreetingMessage: string;
  @attr('boolean') declare voicemailOooTriggerEnabled: boolean;
  @attr('boolean') declare voicemailNoTeammatesAvailableTriggerEnabled: boolean;
  @attr('boolean') declare voicemailMaxQueueSizeTriggerEnabled: boolean;
  @attr('number') declare voicemailMaxQueueSize: number;
  @attr('number') declare portingTicketId: number;
  @attr('string') declare regulatoryBundleSid: string;
  @attr('date') declare freeUsageExpiredAt: Date;
  @attr('string') declare locale: string;
  @attr('boolean') declare recordingConsentEnabled: boolean;
  @attr('string') declare recordingConsentMessage: string;
  @attr('string') declare username: string;
  @attr('string') declare passwordHash: string;
  @attr('string') declare ipRange: string;
  @attr('string') declare externalProvider: PhoneNumberProvider;

  get formattedPhoneNumber() {
    if (this.phoneType === PhoneNumberType.Sip) {
      return this.phoneNumber;
    }
    return parsePhoneNumberFromString(this.phoneNumber)?.formatInternational();
  }

  get assignTeam() {
    let teams = new Map(
      this.appService.app.assignableTeams.map((team: any) => [team.get('id'), team]),
    );
    return teams.get(this.assignTeamId);
  }

  get assignAdmin() {
    return this.appService.app.admins.findBy('id', this.assignAdminId);
  }

  get isActive() {
    return [PhoneNumberStatus.MissingBundle, PhoneNumberStatus.Active].includes(this.status);
  }

  get isPorting() {
    return this.status === PhoneNumberStatus.Porting;
  }

  get isActiveFreeUsageNumber() {
    return this.isActive && this.freeUsageExpiredAt && this.freeUsageExpiredAt > new Date();
  }

  get freeUsageDaysLeft() {
    if (this.freeUsageExpiredAt) {
      let daysLeft = Math.ceil(moment(this.freeUsageExpiredAt).diff(moment(), 'days', true));
      return daysLeft;
    }
    return 0;
  }

  get ipRangeArray() {
    return this.ipRange ? this.ipRange.split(',') : [];
  }

  set ipRangeArray(ipRange: string[]) {
    this.ipRange = ipRange.join(',');
  }

  get isExternalProvider(): boolean {
    return (
      (this.externalProvider !== null && this.externalProvider !== PhoneNumberProvider.Intercom) ||
      this.phoneType === PhoneNumberType.Sip
    );
  }

  get isAmazonConnect(): boolean {
    return this.externalProvider === PhoneNumberProvider.AmazonConnect;
  }
}
